import type { NotificationKind, SearchResultKind, AppUser } from './types';

// Build initials from a full name: "Claire Kemanzi" -> "CK"
export function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Human-readable relative time: "just now", "3m", "2h", "1d", "Jun 14"
export function fmtRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((now.getTime() - then) / 1000);
  if (diffSec < 45)        return 'just now';
  if (diffSec < 60 * 60)   return Math.floor(diffSec / 60) + 'm';
  if (diffSec < 60 * 60 * 24)      return Math.floor(diffSec / 3600) + 'h';
  if (diffSec < 60 * 60 * 24 * 7)  return Math.floor(diffSec / 86400) + 'd';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Cap unread badge: 99+ when too many
export function fmtBadgeCount(n: number): string {
  if (n <= 0) return '';
  if (n > 99) return '99+';
  return String(n);
}

// Which SVG name belongs to each notification kind (used by NotificationsPopover)
export function notificationIconName(kind: NotificationKind): string {
  switch (kind) {
    case 'payroll_ready':      return 'wallet';
    case 'invoice_overdue':    return 'file-warning';
    case 'invoice_paid':       return 'check-circle';
    case 'transaction_review': return 'tag';
    case 'cert_expiring':      return 'shield-check';
    case 'vela_prepared':      return 'sparkles';
    case 'system':             return 'bell';
    default:                   return 'bell';
  }
}

// Which icon shows next to a global search hit
export function searchResultIconName(kind: SearchResultKind): string {
  switch (kind) {
    case 'customer':    return 'user-circle';
    case 'vendor':      return 'briefcase';
    case 'invoice':     return 'file-text';
    case 'bill':        return 'receipt';
    case 'transaction': return 'arrow-left-right';
    case 'employee':    return 'id-card';
    case 'report':      return 'bar-chart';
    case 'page':        return 'file';
    case 'help':        return 'help-circle';
    default:            return 'file';
  }
}

// Build the display user object from a raw API response (defensive)
export function toAppUser(raw: Record<string, unknown> | null | undefined): AppUser | null {
  if (!raw) return null;
  const name = typeof raw.name === 'string' ? raw.name : '';
  const email = typeof raw.email === 'string' ? raw.email : '';
  const id = typeof raw.id === 'string' ? raw.id : String(raw.id ?? '');
  if (!id || !email) return null;
  return {
    id,
    name: name || email,
    email,
    initials: getInitials(name || email),
    avatarUrl: typeof raw.avatar_url === 'string' ? raw.avatar_url : null,
    role: (raw.role as AppUser['role']) || 'owner',
    companyName: typeof raw.company_name === 'string' ? raw.company_name : '',
  };
}
